import {Typography} from '@mui/material'
import {getUtcOffset} from '@hconnect/uikit'
import type {ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {makeStyles} from 'tss-react/mui'

import {BusinessLineType} from '../../../../common/types'
import {RequestedDate, TimezoneWarning} from '../../components'
import {VariationCodeBanner} from '../../components/VariationCodeBanner'
import {useVariationCode} from '../../hooks/useVariationCode'
import type {Order} from '../../Order.types'
import {getEventLocationOffset} from '../../Order.utils'
import {
  VARIATION_CODE_DANGER_COLOR,
  variationCodeCaptionStyle
} from '../../variationCode.utils'

import {DetailsField} from './DetailsField'
import {formattedDate} from './utils'

interface RequestedDateFieldProps {
  order: Order
}

// workaround until RequestedDate component is refactored
const useStyles = makeStyles()(() => ({
  override: {
    '& .MuiTypography-subtitle1': {
      color: '#00274D !important',
      fontSize: '16px !important',
      lineHeight: '16px !important',
      fontWeight: '600 !important'
    },
    '& .MuiTypography-body2': {
      color: '#00274D !important',
      fontSize: '14px !important',
      lineHeight: '8px !important',
      fontWeight: '500 !important'
    }
  }
}))

export const RequestedDateField = ({order}: RequestedDateFieldProps) => {
  const {
    t,
    i18n: {language}
  } = useTranslation()
  const {classes} = useStyles()

  const {shippingDate, schedules, businessLine} = order

  const userOffset = getUtcOffset(shippingDate)
  
  const eventLocationOffset = getEventLocationOffset(order)

  const {
    showDateAsterisk,
    showDateDanger,
    showDateStrikethrough,
    showInlineBanner,
    adjustedVariationTime,
    showRequestedDateTimeWithStrikeThrough,
    variationDescription
  } = useVariationCode(order, eventLocationOffset)

  let value: ReactNode
  let subValue: ReactNode

  if (businessLine === BusinessLineType.RMC) {
    if (!schedules?.length) return null

    const primaryDate = schedules[0].scheduleStartDate

    const requestedDate = formattedDate(
      primaryDate,
      language,
      eventLocationOffset
    )

    value = (
      <span style={{
        ...(showDateStrikethrough && !showRequestedDateTimeWithStrikeThrough
          ? {textDecoration: 'line-through'}
          : {})
      }}>
        {requestedDate[0]}
        {', '}
        {showRequestedDateTimeWithStrikeThrough ? (
          <>
            <span>
              {adjustedVariationTime}
              {showDateAsterisk ? ' *' : ''}
            </span>
            {' '}
            <span style={{textDecoration: 'line-through'}}>{requestedDate[1]}</span>
          </>
        ) : (
          <span style={{
            ...(showDateDanger ? {color: VARIATION_CODE_DANGER_COLOR} : {})
          }}>
            {requestedDate[1]}
            {showDateAsterisk ? ' *' : ''}
          </span>
        )}
      </span>
    )
    subValue = (
      <>
        <TimezoneWarning
          t={t}
          order={order}
          userOffset={userOffset}
          eventLocationOffset={eventLocationOffset}
        />
        {variationDescription && (
          <Typography
            variant="caption"
            style={variationCodeCaptionStyle}
            data-test-id="variation-code-description"
          >
            {'* '}{variationDescription}
          </Typography>
        )}
        {showInlineBanner && (
          <VariationCodeBanner order={order} placement="inline" />
        )}
      </>
    )
  } else {
    value = (
      <div className={classes.override}>
        <RequestedDate
          t={t}
          order={order}
          language={language}
          userOffset={userOffset}
          eventLocationOffset={eventLocationOffset}
        />
      </div>
    )
  }

  return (
    <DetailsField
      label={t('order.orderDetails.requestedDate')}
      dataTestId="requested-date"
      value={value}
      subValue={subValue}
    />
  )
}
